import { t } from 'i18next';
import { SearchX, WandSparkles } from 'lucide-react';
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';

import {
  pieceSelectorUtils,
  PieceGroup,
  VirtualizedItem,
} from '@/app/builder/pieces-selector/piece-selector-utils';
import { kizeoMessaging } from '@/app/routes/embed/kizeo/messaging';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CardListItem, CardListItemSkeleton } from '@/components/ui/card-list';
import { VirtualizedScrollArea } from '@/components/ui/virtualized-scroll-area';
import { PieceIcon } from '@/features/pieces/components/piece-icon';
import {
  StepMetadata,
  PieceSelectorOperation,
  HandleSelectCallback,
  StepMetadataWithSuggestions,
  PieceStepMetadata,
} from '@/features/pieces/lib/types';
import { useIsPieceLocked } from '@/features/policy/lib/locked-pieces';
import { flagsHooks } from '@/hooks/flags-hooks';
import { platformHooks } from '@/hooks/platform-hooks';
import {
  ActionType,
  ApFlagId,
  FlowOperationType,
  TriggerType,
  supportUrl,
} from '@activepieces/shared';

import { cn } from '../../../lib/utils';

import { AskAiButton } from './ask-ai';
import { PieceSearchSuggestions } from './piece-search-suggestions';
import { PieceTagEnum } from './piece-tag-group';

type PiecesCardListProps = {
  debouncedQuery: string;
  selectedTag: PieceTagEnum;
  selectedPieceMetadata: StepMetadata | undefined;
  setSelectedMetadata: (metadata: StepMetadata) => void;
  operation: PieceSelectorOperation;
  handleSelect: HandleSelectCallback;
  pieceGroups: PieceGroup[];
  isLoadingPieces: boolean;
  piecesIsLoaded: boolean;
  noResultsFound: boolean;
  closePieceSelector: () => void;
  showOldVersions: boolean;
  listHeight: number;
};

export const PiecesCardList: React.FC<PiecesCardListProps> = ({
  debouncedQuery,
  selectedPieceMetadata,
  setSelectedMetadata,
  handleSelect,
  pieceGroups,
  isLoadingPieces,
  piecesIsLoaded,
  noResultsFound,
  operation,
  closePieceSelector,
  showOldVersions,
  listHeight,
}) => {
  const { data: showRequestPieceButton } = flagsHooks.useFlag<boolean>(
    ApFlagId.SHOW_COMMUNITY
  );

  const selectedItemRef = useRef<HTMLDivElement | null>(null);
  const isCopilotEnabled = platformHooks.isCopilotEnabled();
  const showActionsOrTriggersInsidePiecesList = debouncedQuery.length > 0;

  const filteredPieceGroups = pieceGroups.map(group => ({
    ...group,
    pieces: group.pieces.filter(piece => {
      if (!showOldVersions && (piece.type === ActionType.PIECE || piece.type === TriggerType.PIECE)) {
        const pieceMetadata = piece as PieceStepMetadata;
        const allPieces = pieceGroups.flatMap(g => g.pieces)
          .filter(p => p.type === ActionType.PIECE || p.type === TriggerType.PIECE)
          .map(p => p as PieceStepMetadata);
        return !pieceSelectorUtils.isOldVersion(pieceMetadata, allPieces);
      }
      return true;
    })
  }));

  const virtualizedItems =
    pieceSelectorUtils.transformPiecesMetadataToVirtualizedItems(
      filteredPieceGroups,
      showActionsOrTriggersInsidePiecesList
    );

  const initialIndexToScrollToInPiecesList = virtualizedItems.findIndex(
    (item: VirtualizedItem) =>
      item.displayName === selectedPieceMetadata?.displayName
  );

  return (
    <div
      className={cn('w-full md:w-[250px] md:min-w-[250px] transition-all', {
        'w-full md:w-full': debouncedQuery.length > 0 || noResultsFound,
      })}
    >
      {isLoadingPieces && (
        <div className="flex flex-col gap-2">
          <CardListItemSkeleton numberOfCards={2} withCircle={false} />
        </div>
      )}

      {piecesIsLoaded && (
        <VirtualizedScrollArea
          key={`${debouncedQuery}`}
          initialScroll={{
            index: initialIndexToScrollToInPiecesList,
            clickAfterScroll: true,
          }}
          items={virtualizedItems}
          estimateSize={(index) => virtualizedItems[index].height}
          getItemKey={(index) => virtualizedItems[index].id}
          listHeight={listHeight}
          renderItem={(item: VirtualizedItem) => {
            if (item.isCategory) {
              return (
                <div className="text-sm text-muted-foreground mx-2 mt-2 h-[28px] flex items-center overflow-hidden">
                  <span className="truncate">{item.displayName}</span>
                </div>
              );
            }
            return (
              <PieceCardListItem
                key={pieceSelectorUtils.toKey(
                  item as StepMetadataWithSuggestions
                )}
                pieceMetadata={item as StepMetadataWithSuggestions}
                selectedPieceMetadata={selectedPieceMetadata}
                debouncedQuery={debouncedQuery}
                setSelectedMetadata={setSelectedMetadata}
                handleSelect={handleSelect}
                ref={
                  item.displayName === selectedPieceMetadata?.displayName
                    ? selectedItemRef
                    : null
                }
                showOldVersions={showOldVersions}
                pieceGroups={pieceGroups}
              />
            );
          }}
        />
      )}

      {noResultsFound &&
        isCopilotEnabled &&
        operation.type !== FlowOperationType.UPDATE_TRIGGER && (
          <div className="flex flex-col gap-2 items-center justify-center h-full ">
            <WandSparkles className="w-14 h-14" />
            <div className="text-sm mb-3">
              {t('Let our AI assistant help you out')}
            </div>
            <AskAiButton
              varitant={'default'}
              operation={operation}
              onClick={closePieceSelector}
            ></AskAiButton>
            {showRequestPieceButton && (
              <>
                {t('Or')}
                <Link
                  to={`${supportUrl}/c/feature-requests/9`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="ghost" size="sm">
                    Request Piece
                  </Button>
                </Link>
              </>
            )}
          </div>
        )}

      {noResultsFound &&
        (!isCopilotEnabled ||
          operation.type === FlowOperationType.UPDATE_TRIGGER) && (
          <div className="flex flex-col gap-2 items-center justify-center h-full ">
            <SearchX className="w-14 h-14" />
            <div className="text-sm ">{t('No pieces found')}</div>
            <div className="text-sm ">{t('Try adjusting your search')}</div>
          </div>
        )}
    </div>
  );
};

export const PieceCardListItem = React.forwardRef<
  HTMLDivElement,
  {
    pieceMetadata: StepMetadataWithSuggestions;
    selectedPieceMetadata: StepMetadata | undefined;
    debouncedQuery: string;
    setSelectedMetadata: (metadata: StepMetadata) => void;
    handleSelect: HandleSelectCallback;
    showOldVersions: boolean;
    pieceGroups: PieceGroup[];
  }
>(
  (
    {
      pieceMetadata,
      selectedPieceMetadata,
      debouncedQuery,
      setSelectedMetadata,
      handleSelect,
      showOldVersions,
      pieceGroups,
    },
    ref,
  ) => {
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const isLocked = useIsPieceLocked(pieceMetadata);

    const openUpgradeModal: HandleSelectCallback = () => {
      kizeoMessaging.sendOpenBusinessModalMessage();
    };

    const handleMouseEnter = (element: HTMLDivElement) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (element.matches(':hover')) {
          setSelectedMetadata(pieceMetadata);
        }
      }, 150);
    };

    const handleMouseLeave = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    const isMobile = pieceSelectorUtils.useIsMobile();
    return (
      <div onMouseLeave={handleMouseLeave} ref={ref} className="h-[49px]">
        <CardListItem
          className={cn("p-3 gap-2 items-center h-full min-h-[49px] max-h-[49px]", {
            "opacity-60": showOldVersions && (pieceMetadata.type === ActionType.PIECE || pieceMetadata.type === TriggerType.PIECE) &&
              pieceSelectorUtils.isOldVersion(pieceMetadata as PieceStepMetadata, pieceGroups.flatMap(g => g.pieces)
                .filter(p => p.type === ActionType.PIECE || p.type === TriggerType.PIECE)
                .map(p => p as PieceStepMetadata))
          })}
          selected={
            pieceMetadata.displayName === selectedPieceMetadata?.displayName &&
            (pieceMetadata.type !== ActionType.PIECE && pieceMetadata.type !== TriggerType.PIECE ? true :
              (pieceMetadata as PieceStepMetadata).pieceVersion === (selectedPieceMetadata as PieceStepMetadata)?.pieceVersion) &&
            debouncedQuery.length === 0
          }
          interactive={debouncedQuery.length === 0}
          onMouseEnter={(e) => handleMouseEnter(e.currentTarget)}
        >
          <div className="flex gap-2 items-center h-full w-full overflow-hidden">
            <div className={isLocked ? 'opacity-50' : undefined}>
              <PieceIcon
                logoUrl={pieceMetadata.logoUrl}
                displayName={pieceMetadata.displayName}
                showTooltip={false}
                size={'sm'}
              />
            </div>
            <div className="flex-grow h-full flex items-center justify-left text-sm overflow-hidden min-w-0 gap-1.5">
              <span className="truncate">
                {pieceMetadata.displayName}
              </span>
              {isLocked && (
                <Badge
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0 flex-shrink-0 bg-[#55c96b]/10 text-[#55c96b]"
                >
                  {t('Business')}
                </Badge>
              )}
              {showOldVersions && (pieceMetadata.type === ActionType.PIECE || pieceMetadata.type === TriggerType.PIECE) && (
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  v{(pieceMetadata as PieceStepMetadata).pieceVersion}
                </span>
              )}
            </div>
          </div>
        </CardListItem>

        {(debouncedQuery.length > 0 || isMobile) &&
          pieceMetadata.type !== TriggerType.EMPTY && (
            <div onMouseEnter={(e) => handleMouseEnter(e.currentTarget)}>
              <PieceSearchSuggestions
                pieceMetadata={pieceMetadata}
                handleSelectOperationSuggestion={
                  isLocked ? openUpgradeModal : handleSelect
                }
                isLocked={isLocked}
              />
            </div>
          )}
      </div>
    );
  },
);

PieceCardListItem.displayName = 'PieceCardListItem';
